import { Country } from '@afterpaytouch/portal-api/types'
import { BiometricsCheckTrigger, CheckoutError, CrossRegionRedirection, InitialData, OtpLoginRouteAndTriggerResult, ConvergenceRollout } from '../../model'
import { AsyncAction, setIntegrationContext } from '../../state'
import { PublicResponse } from '@afterpay/verify-service-api'
import { Route } from '../../config/router'
import { InitialiseNextRoute } from './routing'
import {
  performInitialSetup,
  fetchAndValidateCheckout,
  handlePersistentLogin,
  handleConsolidatedConsumerLookup,
  prepareLookupData,
  performSequentialLookup,
  handleInitialiseError,
  SetupRequest,
  CheckoutFetchRequest,
  ConsumerLookupRequest,
  LookupDataPreparationRequest,
} from './orchestrators'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { isAppleRetailOrder } from '../../state/checkout/helper'
import { getValidUserEmailAndMobile } from '../login-identifier'
import { validateEmailFormat } from './validation'
import { isUndefinedOrEmpty } from '@afterpay/utils'

type InitialiseResult = InitialiseNextRoute | CheckoutError | CrossRegionRedirection | PublicResponse | undefined

export interface InitialiseRequest {
  initialData: InitialData | null
  country: Country
  otpLoginRouteAndTrigger: OtpLoginRouteAndTriggerResult
  biometricsEligibleAndRegisteredCheckTrigger: BiometricsCheckTrigger
  referrer: string | null
  usernameFromCookie: string | null
  skipPrefill: boolean
  rollout: ConvergenceRollout
  logEvent: (event: string, data?: Record<string, unknown>) => void
  cashAuthRoute: Route
}

export const initialise =
  (request: InitialiseRequest): AsyncAction<InitialiseResult> =>
  async (dispatch) => {
    try {
      const setupRequest: SetupRequest = {
        initialData: request.initialData,
        country: request.country,
        referrer: request.referrer,
        isIframe: request.initialData?.isIframe ?? false,
      }

      const initialSetupResult = await performInitialSetup(setupRequest, dispatch)
      if (initialSetupResult.error !== null) {
        capture.warning('Checkout session without token')
        return initialSetupResult.error
      }
      const initialData = initialSetupResult.initialData as InitialData
      const browserCapabilities = {
        canSupportBiometric: initialSetupResult.browserCapabilities?.canSupportBiometric ?? false,
        isAuthenticatorAvailable: initialSetupResult.browserCapabilities?.isAuthenticatorAvailable ?? false,
      }

      const checkoutRequest: CheckoutFetchRequest = { country: request.country, skipPrefill: request.skipPrefill }
      const getCheckoutResponse = await fetchAndValidateCheckout(checkoutRequest, dispatch)

      if ('route' in getCheckoutResponse || 'crossRegionCheckoutUrl' in getCheckoutResponse) {
        return getCheckoutResponse
      }

      const { checkout } = getCheckoutResponse
      const orderSource = checkout?.merchant?.source

      const { email: prefilledEmail } = getValidUserEmailAndMobile({
        crossRegionUserSignInEmail: checkout?.crossRegionUserSignIn?.consumerEmail,
        checkoutEmail: checkout?.consumer?.email,
        checkoutMobile: checkout?.consumer?.phoneNumber,
        usernameFromCookie: request.usernameFromCookie,
      })
      const hasValidPrefilledEmail = !isUndefinedOrEmpty(prefilledEmail) && validateEmailFormat(prefilledEmail)
      const performedRealLookup = hasValidPrefilledEmail || checkout?.consumerLookup != null

      const maybeRedirectToAppleRetailEducationIntro = (result: InitialiseResult): InitialiseResult => {
        if (
          result == null ||
          !('route' in result) ||
          result.route === Route.TERMINAL_ERROR ||
          !isAppleRetailOrder(orderSource) ||
          !performedRealLookup ||
          !('lookup' in result) ||
          result.lookup?.existing !== false
        ) {
          return result
        }

        dispatch(setIntegrationContext({ nextRoute: result.route as Route }))
        return { ...result, route: Route.EDUCATION_INTRO }
      }

      const persistentLoginResult = await handlePersistentLogin(checkout, dispatch)

      if (persistentLoginResult !== null) {
        return maybeRedirectToAppleRetailEducationIntro(persistentLoginResult)
      }

      if (checkout.consumerLookup != null) {
        const consumerLookupRequest: ConsumerLookupRequest = {
          checkout,
          consumerLookup: checkout.consumerLookup,
          logEvent: request.logEvent,
          cashAuthRoute: request.cashAuthRoute,
        }
        const result = await handleConsolidatedConsumerLookup(consumerLookupRequest, dispatch)
        return maybeRedirectToAppleRetailEducationIntro(result)
      }

      const lookupDataRequest: LookupDataPreparationRequest = {
        checkout,
        country: request.country,
        validInitialData: initialData,
        browserCapabilities,
        usernameFromCookie: request.usernameFromCookie,
        biometricsEligibleAndRegisteredCheckTrigger: request.biometricsEligibleAndRegisteredCheckTrigger,
        otpLoginRouteAndTrigger: request.otpLoginRouteAndTrigger,
        rollout: request.rollout,
      }

      const lookupData = prepareLookupData(lookupDataRequest)

      const result = await performSequentialLookup(
        {
          ...lookupData,
          checkout,
        },
        dispatch
      )
      return maybeRedirectToAppleRetailEducationIntro(result)
    } catch (error) {
      return handleInitialiseError(error)
    }
  }
